import { fn, get, hash } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismCard,
  PrismComboSelectInput,
  PrismInput,
  PrismMessage,
  PrismTable,
  PrismTabsContainer,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import { task } from 'ember-concurrency';
import { t, tKey } from 'ember-intl';
import { gt } from 'ember-truth-helpers';
import GeneralDropdownsDropdownPageSize from 'lumen/components/general/dropdowns/dropdown-page-size';
import GeneralDropdownsTriggersTriggerPowerSelectChevron from 'lumen/components/general/dropdowns/triggers/trigger-power-select-chevron';
import GeneralTablesPaginationFooter from 'lumen/components/general/tables/pagination-footer';
import HotelSettingsCentralMappingTablesCellsThAutomationBadge from 'lumen/components/hotel/settings/central-mapping/tables/cells/th-automation-badge';
import HotelSettingsCentralMappingTablesCellsThInventory from 'lumen/components/hotel/settings/central-mapping/tables/cells/th-inventory';
import HotelSettingsCentralMappingTablesCellsThMaxAdults from 'lumen/components/hotel/settings/central-mapping/tables/cells/th-max-adults';
import MarketSettingsCentralMappingTablesCellsTdBarBased from 'lumen/components/market/settings/central-mapping/tables/cells/td-bar-based';
import MarketSettingsCentralMappingTablesCellsTdCommissionable from 'lumen/components/market/settings/central-mapping/tables/cells/td-commissionable';
import MarketSettingsCentralMappingTablesCellsTdDescription from 'lumen/components/market/settings/central-mapping/tables/cells/td-description';
import MarketSettingsCentralMappingTablesCellsTdMapping from 'lumen/components/market/settings/central-mapping/tables/cells/td-mapping';
import MarketSettingsCentralMappingTablesCellsTdNumber from 'lumen/components/market/settings/central-mapping/tables/cells/td-number';
import MarketSettingsCentralMappingTablesCellsTdProperty from 'lumen/components/market/settings/central-mapping/tables/cells/td-property';
import MarketSettingsCentralMappingTablesTableMappingBulkEditRow from 'lumen/components/market/settings/central-mapping/tables/table-mapping/bulk-edit-row';
import SettingsCentralMappingDropdownsDropdownFilter from 'lumen/components/settings/central-mapping/dropdowns/dropdown-filter';
import type {
  AppliedMappingFilterIds,
  MarketFilterOption,
} from 'lumen/components/settings/central-mapping/dropdowns/dropdown-mapping-filter';
import SettingsCentralMappingDropdownsDropdownMappingFilter from 'lumen/components/settings/central-mapping/dropdowns/dropdown-mapping-filter';
import SettingsCentralMappingTablesCellsTdValue from 'lumen/components/settings/central-mapping/tables/cells/td-value';
import type {
  CentralMappingCategory,
  CentralMappingMappingEntryMarketSegment,
  CentralMappingMarketSubscription,
} from 'lumen/models';
import type {
  BarBased,
  Commissionable,
} from 'lumen/models/central-mapping/mapping-entry-market-segment';
import { isMappingEntryRoomType } from 'lumen/models/central-mapping/mapping-entry-room-type';
import {
  type Breakdown,
  BREAKDOWNS,
  BREAKDOWNS_WITHOUT_FILTER_REQUIREMENT,
  type CentralMappingMappingEntryExtendedModel,
  FILTER_OPTIONS,
  type FilterOption,
  isMarketMappingFilterId,
  isPmsValueType,
  LARGE_MARKET_PROPERTY_LIMIT,
  mappingEntryHasBarBased,
  mappingEntryHasCommissionable,
  MARKET_MAPPING_FILTER_IDS,
  type MarketMappingFilter,
  type MarketMappingFilterId,
  MAX_ADULTS_IN_ROOM,
  MAX_ROOM_INVENTORY,
  MAX_USER_FILTERED_PROPERTIES_FOR_EXPORT,
  type NumericValueKeysTypes,
  PMS_VALUE_TYPE_TRANSLATIONS,
  PMS_VALUE_TYPES,
  type PmsValueType,
} from 'lumen/utils/central-mapping-constants';
import { timeout } from 'lumen/utils/waiters';

import styles from './table-mapping.module.css';

const COLUMN_PROPERTIES = {
  ID: 'id',
  VALUE: 'value',
  DESCRIPTION: 'descriptionToDisplay',
  MAPPING: 'mapping',
  PROPERTY: 'property',
  INVENTORY: 'inventory',
  MAX_ADULTS: 'maxAdults',
  BAR_BASED: 'barBased',
  COMMISSIONABLE: 'commissionable',
} as const;

export interface TableMappingRow extends CustomEmberTableRow {
  [COLUMN_PROPERTIES.ID]: string;
  [COLUMN_PROPERTIES.VALUE]: string;
  [COLUMN_PROPERTIES.DESCRIPTION]: string;
  [COLUMN_PROPERTIES.PROPERTY]: string;
  [COLUMN_PROPERTIES.INVENTORY]?: number | null;
  [COLUMN_PROPERTIES.MAX_ADULTS]?: number | null;
  [COLUMN_PROPERTIES.MAPPING]: string;
  autoBarBased?: BarBased;
  autoCategory?: CentralMappingCategory | null;
  autoCommissionable?: Commissionable;
  barBased?: BarBased;
  category?: CentralMappingCategory | null;
  commissionable?: Commissionable;
  isMixedBarBased?: boolean;
  isMixedCategory?: boolean;
  isMixedCommissionable?: boolean;
  setBarBased?: (value: BarBased) => void;
  setCategory?: (cat: CentralMappingCategory | null) => void;
  setCommissionable?: (value: Commissionable) => void;
  userBarBased?: BarBased;
  userCategory?: CentralMappingCategory | null;
  userCommissionable?: Commissionable;
}

// Parent rows have children, and they are solely used to group children together. They don't have a mappingEntry property.
interface TableMappingParentRow extends TableMappingRow {
  children: TableMappingRow[];
  isCollapsed: boolean;
  isMixedNumericCells?: {
    key: NumericValueKeysTypes;
    value: boolean;
  }[];
  isParent: boolean;
  onOverrideAutomapping?: () => void;
  toggleAutomapping?: (category: CentralMappingCategory | null) => void;
}

// Child rows have a mappingEntry property.
interface TableMappingChildRow extends TableMappingRow {
  mappingEntry: CentralMappingMappingEntryExtendedModel;
}

interface MarketSettingsCentralMappingTablesTableMappingSignature {
  Args: {
    appliedFilters?: AppliedMappingFilterIds;
    applyFilters: (appliedFilters: AppliedMappingFilterIds) => void;
    brandOptions?: MarketFilterOption[];
    breakdown: Breakdown;
    chainOptions?: MarketFilterOption[];
    curentPageSizeValueOption: {
      label: string;
      value: number;
    };
    currentPage: number;
    endIndexPage: number;
    filterOption: FilterOption;
    hasUnsavedChanges?: boolean;
    hierarchyCategories: CentralMappingCategory[];
    isLoading: boolean;
    isLoadingMarketFilters: boolean;
    isMappingReadOnly?: boolean;
    isStaffUser?: boolean;
    lastPage: number;
    mapping: CentralMappingMappingEntryExtendedModel[];
    marketMappingFilters: MarketMappingFilter[];
    marketSubscriptions: CentralMappingMarketSubscription[];
    onChangePageSize: (pageSize: number) => void;
    onClickUploadMapping?: () => void;
    onRemoveFilterClicked: (removedFilter: MarketMappingFilter) => void;
    onSetPage: (page: number) => void;
    pageSize: number;
    propertyOptions?: MarketFilterOption[];
    selectedMappingEntries: CentralMappingMappingEntryExtendedModel[];
    selectedRowIds: string[];
    setFilterOption: (filter: FilterOption) => void;
    setMarketMappingFilters: (filters: MarketMappingFilter[]) => void;
    setSelectedMappingEntries: (
      entries: CentralMappingMappingEntryExtendedModel[],
    ) => void;
    setSelectedRowIds: (ids: string[]) => void;
    setValueType: (valueType: PmsValueType) => void;
    startIndexPage: number;
    totalFilteredProperties: number;
    totalNoOfItems: number;
    valueType: PmsValueType;
  };
}

const SEARCH_INPUT_ICONS = {
  SEARCH: 'search',
  CROSS: 'cross',
} as const;

export type SearchInputIconType =
  (typeof SEARCH_INPUT_ICONS)[keyof typeof SEARCH_INPUT_ICONS];

// Type guard used for narrowing the type of a TableMappingRow to a TableMappingParentRow.
const isTableMappingParentRow = (
  row: TableMappingRow | CustomEmberTableRow,
): row is TableMappingParentRow => {
  return 'children' in row;
};

// Type guard used for narrowing the type of a TableMappingRow to a TableMappingChildRow.
const isTableMappingChildRow = (
  row: TableMappingRow | CustomEmberTableRow,
): row is TableMappingChildRow => {
  return 'mappingEntry' in row;
};

export default class MarketSettingsCentralMappingTablesTableMapping extends Component<MarketSettingsCentralMappingTablesTableMappingSignature> {
  @service declare centralMapping: Services['central-mapping'];
  @service declare intl: Services['intl'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked expandedRows: string[] = [];
  @tracked isMappingDropdownOpen = false;
  @tracked searchInputValue = '';

  onSearchInputTask = task(
    { restartable: true },
    async (event: Event): Promise<void> => {
      this.searchInputValue = (event.target as HTMLInputElement)?.value;

      this.persistSelectedRows();

      await timeout('extra-long');
      this.usageAnalytics.track('Mapping Data Searched', {
        eventTrigger: 'search-mapping',
      });
    },
  );

  get columns(): CustomEmberTableColumn[] {
    const isMarketSegmentBreakdown =
      this.args.breakdown === BREAKDOWNS.MARKET_SEGMENT;
    const isRoomTypeBreakdown = this.args.breakdown === BREAKDOWNS.ROOM_TYPE;

    const cols: CustomEmberTableColumn[] = [];

    cols.push({
      name: this.intl.t('general.value'),
      textAlign: 'left' as const,
      tdComponent: SettingsCentralMappingTablesCellsTdValue,
      valuePath: COLUMN_PROPERTIES.VALUE,
      width: 150,
      isReadOnly: this.isMappingReadOnly,
    });

    cols.push({
      name: this.intl.t('general.description'),
      textAlign: 'left' as const,
      tdComponent: MarketSettingsCentralMappingTablesCellsTdDescription,
      valuePath: COLUMN_PROPERTIES.DESCRIPTION,
      width: 200,
      isReadOnly: this.isMappingReadOnly,
    });

    cols.push({
      name: this.intl.t('settings.central_mapping.property'),
      textAlign: 'left' as const,
      tdComponent: MarketSettingsCentralMappingTablesCellsTdProperty,
      valuePath: COLUMN_PROPERTIES.PROPERTY,
      width: 200,
      isReadOnly: this.isMappingReadOnly,
    });

    const showAutoMappingBadge =
      this.args.valueType === PMS_VALUE_TYPES.RATE_CODE &&
      !this.args.isLoading &&
      !this.args.isMappingReadOnly;

    cols.push({
      name: this.intl.t('general.mapping'),
      textAlign: 'left' as const,
      tdComponent: MarketSettingsCentralMappingTablesCellsTdMapping,
      thComponent: showAutoMappingBadge
        ? HotelSettingsCentralMappingTablesCellsThAutomationBadge
        : undefined,
      tdClassNames: 'table-column--with-possible-unmapped-background',
      valuePath: COLUMN_PROPERTIES.MAPPING,
      hierarchyCategories: this.args.hierarchyCategories,
      width: 400,
      isReadOnly: this.isMappingReadOnly,
    });

    if (isMarketSegmentBreakdown) {
      cols.push({
        name: this.intl.t('settings.central_mapping.mapping.bar_based'),
        textAlign: 'left' as const,
        tdComponent: MarketSettingsCentralMappingTablesCellsTdBarBased,
        thComponent:
          this.args.valueType === PMS_VALUE_TYPES.RATE_CODE
            ? HotelSettingsCentralMappingTablesCellsThAutomationBadge
            : undefined,
        valuePath: COLUMN_PROPERTIES.BAR_BASED,
        width: 200,
        isReadOnly: this.isMappingReadOnly,
      });

      // Commissionable only applies to rate plans (the rate_code value type).
      if (this.args.valueType === PMS_VALUE_TYPES.RATE_CODE) {
        cols.push({
          name: this.intl.t('settings.central_mapping.mapping.commissionable'),
          textAlign: 'left' as const,
          tdComponent: MarketSettingsCentralMappingTablesCellsTdCommissionable,
          thComponent: showAutoMappingBadge
            ? HotelSettingsCentralMappingTablesCellsThAutomationBadge
            : undefined,
          valuePath: COLUMN_PROPERTIES.COMMISSIONABLE,
          width: 200,
          isReadOnly: this.isMappingReadOnly,
        });
      }
    }

    if (isRoomTypeBreakdown) {
      cols.push({
        name: this.intl.t('settings.central_mapping.mapping.inventory'),
        textAlign: 'left' as const,
        thComponent: HotelSettingsCentralMappingTablesCellsThInventory,
        tdComponent: MarketSettingsCentralMappingTablesCellsTdNumber,
        valuePath: COLUMN_PROPERTIES.INVENTORY,
        width: 150,
        isReadOnly: this.isMappingReadOnly,
        maxValue: MAX_ROOM_INVENTORY,
        valueKey: COLUMN_PROPERTIES.INVENTORY,
        placeholder: this.intl.t(
          'settings.central_mapping.mapping.number_of_rooms',
        ),
        tdNumberDataTestId: 'central-mapping-td-number-inventory',
      });

      cols.push({
        key: COLUMN_PROPERTIES.ID,
        name: this.intl.t('settings.central_mapping.mapping.max_adults'),
        textAlign: 'left' as const,
        thComponent: HotelSettingsCentralMappingTablesCellsThMaxAdults,
        tdComponent: MarketSettingsCentralMappingTablesCellsTdNumber,
        valuePath: COLUMN_PROPERTIES.MAX_ADULTS,
        width: 150,
        isReadOnly: this.isMappingReadOnly,
        maxValue: MAX_ADULTS_IN_ROOM,
        valueKey: COLUMN_PROPERTIES.MAX_ADULTS,
        placeholder: this.intl.t(
          'settings.central_mapping.mapping.max_adults.placeholder',
        ),
        tdNumberDataTestId: 'central-mapping-td-number-max-adults',
      });
    }

    return cols;
  }

  get exportButtonTooltipText(): string {
    const { totalFilteredProperties, hasUnsavedChanges } = this.args;

    if (hasUnsavedChanges) {
      return this.intl.t('settings.central-mapping.mapping.export.tooltip');
    }

    if (totalFilteredProperties > MAX_USER_FILTERED_PROPERTIES_FOR_EXPORT) {
      return this.intl.t(
        'settings.central-mapping.mapping.export.tooltip-filter-properties',
        {
          maxProperties: MAX_USER_FILTERED_PROPERTIES_FOR_EXPORT,
          totalProperties: totalFilteredProperties,
        },
      );
    }
    return '';
  }

  // Cached decorator gives a big performance boost when bulk mapping many table rows.
  @cached
  get filteredMapping(): CentralMappingMappingEntryExtendedModel[] {
    return (
      this.args.mapping
        // eslint-disable-next-line @typescript-eslint/unbound-method
        .filter(this.filterRowsByMarketMappingFilters)
        // eslint-disable-next-line @typescript-eslint/unbound-method
        .filter(this.filterRowsByDropdownOption)
        // eslint-disable-next-line @typescript-eslint/unbound-method
        .filter(this.filterRowsBySearch)
    );
  }

  get formattedEndIndexPage(): string {
    return this.intl.formatNumber(this.args.endIndexPage);
  }

  get formattedStartIndexPage(): string {
    return this.intl.formatNumber(this.args.startIndexPage);
  }

  get formattedTotalNoOfItems(): string {
    return this.intl.formatNumber(this.args.totalNoOfItems);
  }

  get hasActivePropertyFilters(): boolean {
    const { appliedFilters } = this.args;
    if (!appliedFilters) {
      return false;
    }
    const { brandIds = [], chainIds = [], propertyIds = [] } = appliedFilters;
    return brandIds.length > 0 || chainIds.length > 0 || propertyIds.length > 0;
  }

  get isExportDisabled(): boolean {
    const { totalFilteredProperties, isLoading, hasUnsavedChanges } = this.args;

    return (
      isLoading ||
      hasUnsavedChanges ||
      this.showFilterNudgeEmptyState ||
      totalFilteredProperties > MAX_USER_FILTERED_PROPERTIES_FOR_EXPORT
    );
  }

  get isMappingReadOnly(): boolean {
    return Boolean(this.args.isMappingReadOnly);
  }

  /**
   * Add the `cached` decorator to this getter, otherwise we can't unselect rows in the table.
   * The decorator preserves references for equality checks in ember-table.
   */
  @cached
  get rows(): TableMappingRow[] {
    if (this.showFilterNudgeEmptyState) {
      return [];
    }

    // Convert the mapping entries to table rows.
    const mappingEntryTableRows = this.filteredMapping.map((mappingEntry) =>
      this.mappingEntryToTableRow(mappingEntry),
    );

    // Group rows with the same value together.
    // The key is the value of the mapping entry, and the value is an array of rows with that value.
    const valueAndMappingEntryRows: Map<string, TableMappingChildRow[]> =
      new Map();

    mappingEntryTableRows.forEach((row) => {
      if (!row.value) {
        row.value = this.intl.t('general.missing');
      }
      const key = row.value.toUpperCase();
      if (!valueAndMappingEntryRows.has(key)) {
        valueAndMappingEntryRows.set(key, []);
      }
      valueAndMappingEntryRows.get(key)?.push(row);
    });

    // Create a new array of rows where rows with the same value are grouped together to form collapsable parent rows.
    const groupedMappingEntryRows: TableMappingRow[] = [];

    valueAndMappingEntryRows.forEach(
      (mappingEntryRowsWithSameValue, mappingEntryValue) => {
        // If there is only one row with the same value, add it directly to the array of rows.
        if (
          mappingEntryRowsWithSameValue.length === 1 &&
          mappingEntryRowsWithSameValue[0]
        ) {
          groupedMappingEntryRows.push(mappingEntryRowsWithSameValue[0]);
          return;
        }

        // If there are multiple rows with the same value, create a parent row that groups them together. The collapsable row.
        const childMappingEntries = mappingEntryRowsWithSameValue.map(
          (child) => child.mappingEntry,
        );

        // If all children have the same category, set the parent category to that category.
        const sameCategoryChildMappingEntry =
          this.computeParentRowCategoryMappingEntry(childMappingEntries);
        const parentCategoryPath =
          sameCategoryChildMappingEntry?.category?.get('path') ?? [];
        const parentMappingSortString = parentCategoryPath
          .map((cat) => cat.name)
          .join();

        const parentRowId = `${mappingEntryValue}-parent`;
        const isCollapsed = !this.expandedRows.includes(parentRowId);

        const parentRow: TableMappingParentRow = {
          [COLUMN_PROPERTIES.ID]: parentRowId,
          isParent: true,
          isCollapsed,
          [COLUMN_PROPERTIES.VALUE]: mappingEntryValue,
          [COLUMN_PROPERTIES.DESCRIPTION]:
            this.getParentRowDescriptionValue(childMappingEntries) ??
            this.intl.t('general.missing'),
          [COLUMN_PROPERTIES.PROPERTY]: `${mappingEntryRowsWithSameValue.length}  ${this.intl.t('settings.central_mapping.hotels')}`,
          children: mappingEntryRowsWithSameValue,
          [COLUMN_PROPERTIES.MAPPING]: parentMappingSortString ?? '',
        };

        const isMixedNumericCells = new Map<NumericValueKeysTypes, boolean>();

        const parentInventoryValue = this.getParentNumberValue(
          mappingEntryRowsWithSameValue,
          COLUMN_PROPERTIES.INVENTORY,
          'sum',
        );
        parentRow[COLUMN_PROPERTIES.INVENTORY] = parentInventoryValue?.value;

        const parentMaxAdultsValue = this.getParentNumberValue(
          mappingEntryRowsWithSameValue,
          COLUMN_PROPERTIES.MAX_ADULTS,
          'common',
        );
        if (parentMaxAdultsValue) {
          isMixedNumericCells.set(
            COLUMN_PROPERTIES.MAX_ADULTS,
            Boolean(parentMaxAdultsValue.isMixed),
          );
          parentRow[COLUMN_PROPERTIES.MAX_ADULTS] = parentMaxAdultsValue.value;
        }

        parentRow.isMixedNumericCells = Array.from(
          isMixedNumericCells.entries(),
        ).map(([key, value]) => ({
          key,
          value,
        }));

        parentRow.category = sameCategoryChildMappingEntry?.category;
        parentRow.isMixedCategory =
          this.getParentIsMixedCategory(childMappingEntries);
        parentRow.userCategory = sameCategoryChildMappingEntry?.userCategory;
        parentRow.setCategory = (cat: CentralMappingCategory | null): void => {
          const isSameCategory =
            childMappingEntries[0]?.category?.name === cat?.name;

          this.setCategory(childMappingEntries, cat);

          if (!isSameCategory) {
            this.centralMapping.trackMappingEntryChanged({
              mappingChange: 'mapping',
              rowType: 'parent',
              rowCount: childMappingEntries.length,
            });
          }
        };
        parentRow.toggleAutomapping = (
          category: CentralMappingCategory | null,
        ): void => {
          this.setCategory(childMappingEntries, category);

          if (category) {
            this.usageAnalytics.track('Mapping Automation Disabled', {
              eventTrigger: 'disable-automapping',
              mappingChange: 'mapping',
            });
          }
        };
        parentRow.onOverrideAutomapping = (): void => {
          this.usageAnalytics.track('Mapping Automation Overwritten', {
            eventTrigger: 'override-automapping',
            mappingChange: 'mapping',
          });
        };

        if (childMappingEntries[0]) {
          /*
          If all of the children have the same value, and atleast one is auto,
          set the parent row's autoCategory to that value and remove the userCategory value.
          */
          parentRow.autoCategory =
            this.getParentAutoCategory(childMappingEntries);
          if (!isEmpty(parentRow.autoCategory)) {
            parentRow.userCategory = null;
          }

          // @ts-expect-error: Incorrect type
          if (mappingEntryHasBarBased(childMappingEntries[0])) {
            const childMappingEntriesWithBarBased = childMappingEntries.filter(
              // @ts-expect-error: Incorrect type
              (childMappingEntry) => mappingEntryHasBarBased(childMappingEntry),
            );

            const parentRowBarBasedMappingEntry =
              this.computeParentRowBarBasedMappingEntry(
                childMappingEntriesWithBarBased,
              );

            parentRow.barBased = parentRowBarBasedMappingEntry?.barBased;

            /*
            If all of the children have the same value, and atleast one is auto,
            set the parent row's autoBarBased to that value and remove the userBarBased value.
            */
            parentRow.autoBarBased = this.getParentAutoBarBased(
              childMappingEntriesWithBarBased,
            );
            if (!isEmpty(parentRow.autoBarBased)) {
              parentRow.userBarBased = null;
            } else {
              parentRow.userBarBased = this.isAnyChildRowBarBasedUserBarBased(
                childMappingEntriesWithBarBased,
              )
                ? parentRowBarBasedMappingEntry?.userBarBased
                : null;
            }

            parentRow.isMixedBarBased = this.getParentIsMixedBarBased(
              childMappingEntriesWithBarBased,
            );
            parentRow.setBarBased = (value: BarBased): void => {
              this.setBarBased(childMappingEntriesWithBarBased, value);

              this.centralMapping.trackMappingEntryChanged({
                mappingChange: 'barBased',
                rowType: 'parent',
                rowCount: childMappingEntriesWithBarBased.length,
              });
            };
          }

          if (mappingEntryHasCommissionable(childMappingEntries[0])) {
            const childMappingEntriesWithCommissionable =
              childMappingEntries.filter((childMappingEntry) =>
                mappingEntryHasCommissionable(childMappingEntry),
              );

            const parentRowCommissionableMappingEntry =
              this.computeParentRowCommissionableMappingEntry(
                childMappingEntriesWithCommissionable,
              );

            parentRow.commissionable =
              parentRowCommissionableMappingEntry?.commissionable;

            /*
            If all of the children have the same value, and atleast one is auto,
            set the parent row's autoCommissionable to that value and remove the userCommissionable value.
            */
            parentRow.autoCommissionable = this.getParentAutoCommissionable(
              childMappingEntriesWithCommissionable,
            );
            if (!isEmpty(parentRow.autoCommissionable)) {
              parentRow.userCommissionable = null;
            } else {
              parentRow.userCommissionable =
                this.isAnyChildRowCommissionableUserCommissionable(
                  childMappingEntriesWithCommissionable,
                )
                  ? parentRowCommissionableMappingEntry?.userCommissionable
                  : null;
            }

            parentRow.isMixedCommissionable =
              this.getParentIsMixedCommissionable(
                childMappingEntriesWithCommissionable,
              );
            parentRow.setCommissionable = (value: Commissionable): void => {
              this.setCommissionable(
                childMappingEntriesWithCommissionable,
                value,
              );

              this.centralMapping.trackMappingEntryChanged({
                mappingChange: 'commissionable',
                rowType: 'parent',
                rowCount: childMappingEntriesWithCommissionable.length,
              });
            };
          }
        }

        groupedMappingEntryRows.push(parentRow);
      },
    );

    return groupedMappingEntryRows;
  }

  get searchIcon(): {
    name: SearchInputIconType;
    onClick: (() => void) | undefined;
  } {
    const isEditing = this.searchInputValue.length > 0;
    let name: SearchInputIconType = SEARCH_INPUT_ICONS.SEARCH;
    if (isEditing) {
      name = SEARCH_INPUT_ICONS.CROSS;
    }

    const onClick = isEditing
      ? (): void => this.manageSearchIconClick(name)
      : undefined;

    return { name, onClick };
  }

  get selectedRows(): TableMappingRow[] {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      this.rows
        .flatMap((rowItem) => {
          const rowChildren = Array.isArray(rowItem['children'])
            ? rowItem['children']
            : [];
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-return
          return [rowItem, ...rowChildren];
        })
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        .filter((item) => this.args.selectedRowIds.includes(item.id))
    );
  }

  get showCommissionableFilter(): boolean {
    return (
      this.args.breakdown === BREAKDOWNS.MARKET_SEGMENT &&
      this.args.valueType === PMS_VALUE_TYPES.RATE_CODE
    );
  }

  get showExportTooltip(): boolean {
    const { totalFilteredProperties, isLoading, hasUnsavedChanges } = this.args;

    if (isLoading) {
      return false;
    }

    return (
      hasUnsavedChanges ||
      totalFilteredProperties > MAX_USER_FILTERED_PROPERTIES_FOR_EXPORT
    );
  }

  get showFilterNudgeEmptyState(): boolean {
    const { propertyOptions, breakdown } = this.args;

    if (
      propertyOptions &&
      propertyOptions.length < LARGE_MARKET_PROPERTY_LIMIT
    ) {
      return false;
    }

    return (
      !BREAKDOWNS_WITHOUT_FILTER_REQUIREMENT.includes(breakdown) &&
      !this.hasActivePropertyFilters
    );
  }

  get showingText(): string {
    return this.intl.t('settings.central-mapping.mapping.page_showing', {
      currentPage: this.formattedStartIndexPage,
      lastPage: this.formattedEndIndexPage,
      totalRecords: this.formattedTotalNoOfItems,
    });
  }

  get showPaginationText(): number | false {
    if (this.showFilterNudgeEmptyState) {
      return false;
    }

    const { totalNoOfItems, startIndexPage, endIndexPage } = this.args;
    return (
      totalNoOfItems &&
      startIndexPage &&
      endIndexPage &&
      this.filteredMapping.length
    );
  }

  get uploadDropdownOptions(): { icon: 'upload'; id: string; label: string }[] {
    return [
      {
        id: 'upload-mapping',
        label: this.intl.t('settings.central_mapping.upload_mapping'),
        icon: 'upload',
      },
    ];
  }

  get visibleTabIds(): PmsValueType[] {
    const breakdownRecord = this.centralMapping.breakdowns.find(
      (b) => b.breakdown === this.args.breakdown,
    );

    if (!breakdownRecord) {
      return [];
    }

    return breakdownRecord.valueTypes
      .slice()
      .filter((valueTypeRecord) => valueTypeRecord.available)
      .map((valueTypeRecord) => valueTypeRecord.valueType)
      .sort(
        (a, b) =>
          Object.values(PMS_VALUE_TYPES).indexOf(a) -
          Object.values(PMS_VALUE_TYPES).indexOf(b),
      );
  }

  clearRowSelections(): void {
    this.args.setSelectedMappingEntries([]);
    this.args.setSelectedRowIds([]);
  }

  computeParentRowBarBasedMappingEntry(
    childMappings: CentralMappingMappingEntryMarketSegment[],
  ): CentralMappingMappingEntryMarketSegment | null {
    if (!childMappings[0]) {
      return null;
    }

    const firstBarBased = childMappings[0].barBased;

    if (childMappings.every((entry) => entry.barBased === firstBarBased)) {
      return childMappings[0];
    }

    return null;
  }

  computeParentRowCategoryMappingEntry(
    childMappings: CentralMappingMappingEntryExtendedModel[],
  ): CentralMappingMappingEntryExtendedModel | null {
    if (!childMappings[0]) {
      return null;
    }

    const firstCategory = childMappings[0].category;

    if (childMappings.every((entry) => entry.category === firstCategory)) {
      return childMappings[0];
    }

    return null;
  }

  computeParentRowCommissionableMappingEntry(
    childMappings: CentralMappingMappingEntryMarketSegment[],
  ): CentralMappingMappingEntryMarketSegment | null {
    if (!childMappings[0]) {
      return null;
    }

    const firstCommissionable = childMappings[0].commissionable;

    if (
      childMappings.every(
        (entry) => entry.commissionable === firstCommissionable,
      )
    ) {
      return childMappings[0];
    }

    return null;
  }

  getMappingEntriesFromSelectedRows(
    rows: TableMappingRow[],
  ): CentralMappingMappingEntryExtendedModel[] {
    const mappingEntries: CentralMappingMappingEntryExtendedModel[] = [];

    rows.forEach((row) => {
      if (isTableMappingParentRow(row)) {
        const { children } = row;
        mappingEntries.push(
          ...this.getMappingEntriesFromSelectedRows(children),
        );
      } else if (isTableMappingChildRow(row)) {
        mappingEntries.push(row['mappingEntry']);
      }
    });

    return mappingEntries;
  }

  getParentAutoBarBased(
    childMappings: CentralMappingMappingEntryMarketSegment[],
  ): BarBased | null {
    if (!childMappings[0]) {
      return null;
    }
    // If all children have the same barBased, and one or more are auto-mapped, set the parent as auto-mapped.
    const autoBarBased = childMappings
      .filter((entry) => {
        return !isEmpty(entry.autoBarBased) && isEmpty(entry.userBarBased);
      })
      .map((entry) => entry.autoBarBased);

    const uniqueAutoBarBased = Array.from(new Set(autoBarBased));
    if (uniqueAutoBarBased.length === 1) {
      return uniqueAutoBarBased[0] ?? null;
    }

    return null;
  }

  getParentAutoCategory(
    childMappings: CentralMappingMappingEntryExtendedModel[],
  ): CentralMappingCategory | null | undefined {
    if (!childMappings[0]) {
      return null;
    }

    // If all children have the same category, and one or more are auto-mapped, set the parent as auto-mapped.
    const autoCategories = childMappings
      .filter((entry) => {
        return isEmpty(entry.userCategory) && !isEmpty(entry.autoCategory);
      })
      .map((entry) => entry.autoCategory);

    const uniqueAutoCategories = Array.from(new Set(autoCategories));
    if (uniqueAutoCategories.length === 1) {
      return uniqueAutoCategories[0];
    }

    return null;
  }

  getParentAutoCommissionable(
    childMappings: CentralMappingMappingEntryMarketSegment[],
  ): Commissionable | null {
    if (!childMappings[0]) {
      return null;
    }
    // If all children have the same commissionable, and one or more are auto-mapped, set the parent as auto-mapped.
    const autoCommissionable = childMappings
      .filter((entry) => {
        return (
          !isEmpty(entry.autoCommissionable) &&
          isEmpty(entry.userCommissionable)
        );
      })
      .map((entry) => entry.autoCommissionable);

    const uniqueAutoCommissionable = Array.from(new Set(autoCommissionable));
    if (uniqueAutoCommissionable.length === 1) {
      return uniqueAutoCommissionable[0] ?? null;
    }

    return null;
  }

  getParentIsMixedBarBased(
    childMappings: CentralMappingMappingEntryMarketSegment[],
  ): boolean {
    if (!childMappings[0]) {
      return false;
    }

    const firstBarBased = childMappings[0]?.barBased;

    if (childMappings.every((entry) => entry.barBased === firstBarBased)) {
      return false;
    }

    return true;
  }

  getParentIsMixedCategory(
    childMappings: CentralMappingMappingEntryExtendedModel[],
  ): boolean {
    if (!childMappings[0]) {
      return false;
    }

    const firstCategory = childMappings[0]?.category;

    if (childMappings.every((entry) => entry.category === firstCategory)) {
      return false;
    }

    return true;
  }

  getParentIsMixedCommissionable(
    childMappings: CentralMappingMappingEntryMarketSegment[],
  ): boolean {
    if (!childMappings[0]) {
      return false;
    }

    const firstCommissionable = childMappings[0]?.commissionable;

    if (
      childMappings.every(
        (entry) => entry.commissionable === firstCommissionable,
      )
    ) {
      return false;
    }

    return true;
  }

  getParentNumberValue(
    children: TableMappingRow[],
    key: string = COLUMN_PROPERTIES.INVENTORY,
    operator: 'sum' | 'common' = 'common',
  ): {
    isMixed?: boolean;
    value: number | null;
  } {
    const isMissingValue = children.every((child) => isEmpty(child[key]));
    if (isMissingValue) {
      return {
        value: null,
      };
    }

    // If the operator is 'sum', we sum all the values of the children.
    if (operator === 'sum') {
      const sum = children.reduce((acc, child) => {
        const value = child[key];
        return acc + (typeof value === 'number' ? value : 0);
      }, 0);

      return {
        value: sum,
      };
    }

    // If the operator is 'common', we check if all the values are the same and return that value if they are. Otherwise, we return null.
    const firstValue = children[0] ? (children[0][key] as number) : null;

    if (children.every((child) => child[key] === firstValue)) {
      return {
        value: firstValue,
      };
    }

    return {
      value: null,
      isMixed: true,
    };
  }

  getParentRowDescriptionValue(
    childMappings: CentralMappingMappingEntryExtendedModel[],
  ): string {
    const descriptions = childMappings?.map((child) => {
      return child[COLUMN_PROPERTIES.DESCRIPTION] ?? '';
    });
    const uniqueDescriptions = Array.from(new Set(descriptions));

    if (uniqueDescriptions.length === 1) {
      return uniqueDescriptions[0] ?? '';
    }
    return this.intl.t('settings.central_mapping.mapping.mixed');
  }

  isAnyChildRowBarBasedUserBarBased(
    childMappings: CentralMappingMappingEntryMarketSegment[],
  ): boolean {
    return childMappings.some((entry) => !isEmpty(entry.userBarBased));
  }

  isAnyChildRowCommissionableUserCommissionable(
    childMappings: CentralMappingMappingEntryMarketSegment[],
  ): boolean {
    return childMappings.some((entry) => !isEmpty(entry.userCommissionable));
  }

  manageSearchIconClick(name: SearchInputIconType): void {
    if (name === SEARCH_INPUT_ICONS.CROSS) {
      this.searchInputValue = '';
    }
  }

  mappingEntryToTableRow(
    mappingEntry: CentralMappingMappingEntryExtendedModel,
  ): TableMappingChildRow {
    const { breakdown } = this.args;
    // For sorting: to sort on the mapping column, we join the category path into 1 string that we can sort on.
    // E.g. ['Transient', 'my custom category', 'my custom subcategory'] becomes 'Transient,my custom category,my custom subcategory'.
    const categoryPath = mappingEntry.category?.get('path') ?? [];
    const mappingSortString = categoryPath.map((cat) => cat.name).join();

    const row: TableMappingChildRow = {
      // @ts-expect-error: ype of computed property's value is 'string | null', which is not assignable to type 'string'.
      [COLUMN_PROPERTIES.ID]: mappingEntry.id,
      className: mappingEntry.isUnmapped ? 'table-row--unmapped' : '',
      [COLUMN_PROPERTIES.VALUE]: mappingEntry[COLUMN_PROPERTIES.VALUE],
      [COLUMN_PROPERTIES.DESCRIPTION]:
        mappingEntry[COLUMN_PROPERTIES.DESCRIPTION],
      [COLUMN_PROPERTIES.MAPPING]: mappingSortString,
      [COLUMN_PROPERTIES.PROPERTY]: mappingEntry.hotelName,
      category: mappingEntry.category,
      userCategory: mappingEntry.userCategory,
      mappingEntry,
      toggleAutomapping: (category: CentralMappingCategory | null) => {
        this.setCategory([mappingEntry], category);
        const mappingChange = category
          ? 'automapping-disabled'
          : 'automapping-enabled';

        if (category) {
          this.usageAnalytics.track('Mapping Automation Disabled', {
            eventTrigger: 'disable-automapping',
            mappingChange: 'mapping',
          });
        }
        this.centralMapping.trackMappingEntryChanged({ mappingChange });
      },
      onOverrideAutomapping: () => {
        this.usageAnalytics.track('Mapping Automation Overwritten', {
          eventTrigger: 'override-automapping',
          mappingChange: 'mapping',
        });
      },
      setCategory: (cat: CentralMappingCategory | null) => {
        const isSameCategory = mappingEntry?.category === cat;
        this.setCategory([mappingEntry], cat);

        if (!isSameCategory) {
          this.centralMapping.trackMappingEntryChanged({
            mappingChange: 'mapping',
          });
        }
      },
      setBarBased: (value: boolean | null) => {
        this.setBarBased([mappingEntry], value);

        this.centralMapping.trackMappingEntryChanged({
          mappingChange: 'barBased',
        });
      },
      setCommissionable: (value: Commissionable) => {
        this.setCommissionable([mappingEntry], value);

        this.centralMapping.trackMappingEntryChanged({
          mappingChange: 'commissionable',
        });
      },
    };

    // @ts-expect-error: Incorrect type
    if (mappingEntryHasBarBased(mappingEntry)) {
      row[COLUMN_PROPERTIES.BAR_BASED] =
        mappingEntry[COLUMN_PROPERTIES.BAR_BASED];

      row.userBarBased = mappingEntry.userBarBased;
      row.autoBarBased = mappingEntry.autoBarBased;
    }

    if (mappingEntryHasCommissionable(mappingEntry)) {
      row[COLUMN_PROPERTIES.COMMISSIONABLE] =
        mappingEntry[COLUMN_PROPERTIES.COMMISSIONABLE];

      row.userCommissionable = mappingEntry.userCommissionable;
      row.autoCommissionable = mappingEntry.autoCommissionable;
    }

    row.autoCategory = mappingEntry.autoCategory;

    if (
      breakdown === BREAKDOWNS.ROOM_TYPE &&
      // @ts-expect-error: Incorrect type
      isMappingEntryRoomType(mappingEntry)
    ) {
      row[COLUMN_PROPERTIES.INVENTORY] =
        mappingEntry[COLUMN_PROPERTIES.INVENTORY];
      row[COLUMN_PROPERTIES.MAX_ADULTS] =
        mappingEntry[COLUMN_PROPERTIES.MAX_ADULTS];
    }

    return row;
  }

  persistSelectedRows(): void {
    this.args.setSelectedRowIds(this.selectedRows.map((row) => row.id));
  }

  @action applyMappingFilters(appliedFilters: AppliedMappingFilterIds): void {
    this.isMappingDropdownOpen = false;
    this.args.applyFilters(appliedFilters);
  }

  @action
  filterRowsByDropdownOption(
    row: CentralMappingMappingEntryExtendedModel,
  ): boolean {
    const { filterOption } = this.args;

    switch (filterOption) {
      case FILTER_OPTIONS.UNMAPPED:
        return row.isUnmapped;
      case FILTER_OPTIONS.AUTOMATED:
        return row.isAutomapped;
      case FILTER_OPTIONS.UNSAVED:
        return row.isUnsaved;
      case FILTER_OPTIONS.ALL:
      default:
        return true;
    }
  }

  @action
  filterRowsByMarketMappingFilters(
    row: CentralMappingMappingEntryExtendedModel,
  ): boolean {
    const { marketMappingFilters } = this.args;

    if (marketMappingFilters.length === 0) {
      return true;
    }

    const filterGroups: Partial<
      Record<MarketMappingFilterId, MarketMappingFilter[]>
    > = {};
    marketMappingFilters.forEach((filter) => {
      const filterGroup = filterGroups[filter.id];

      if (!filterGroup) {
        filterGroups[filter.id] = [filter];
      } else {
        filterGroup.push(filter);
      }
    });

    return Object.entries(filterGroups).every(([filterId, filterValues]) => {
      if (!isMarketMappingFilterId(filterId)) {
        return true;
      }

      return filterValues.some((filter) => {
        switch (filter.id) {
          case MARKET_MAPPING_FILTER_IDS.HOTEL_NAME:
            return row.hotelName === filter.label;
          case MARKET_MAPPING_FILTER_IDS.CHAIN:
            return row.chains.includes(filter.label);
          case MARKET_MAPPING_FILTER_IDS.BRAND:
            return row.brands.includes(filter.label);
          default:
            return false;
        }
      });
    });
  }

  @action
  filterRowsBySearch(
    mappingEntry: CentralMappingMappingEntryExtendedModel,
  ): boolean {
    // Convert strings to lowercase to make the filtering case-insensitive
    const searchInputValueLowercase = this.searchInputValue
      .toLowerCase()
      .trim();

    const valueLowercase = mappingEntry[COLUMN_PROPERTIES.VALUE]
      .toLowerCase()
      .trim();
    const descriptionLowercase = mappingEntry[COLUMN_PROPERTIES.DESCRIPTION]
      .toLowerCase()
      .trim();

    const valueIncludesSearchTerm = valueLowercase.includes(
      searchInputValueLowercase,
    );
    const descriptionIncludesSearchTerm = descriptionLowercase.includes(
      searchInputValueLowercase,
    );

    return valueIncludesSearchTerm || descriptionIncludesSearchTerm;
  }

  @action
  getTabTranslationKey(tabId: string, breakdown: Breakdown): string {
    if (
      tabId === PMS_VALUE_TYPES.RATE_CODE &&
      breakdown === BREAKDOWNS.RATE_CODE
    ) {
      return tKey('settings.central_mapping.package');
    }
    return PMS_VALUE_TYPE_TRANSLATIONS[tabId as PmsValueType] ?? '';
  }

  @action handleMappingExport(): void {
    const { valueType, breakdown, appliedFilters } = this.args;

    this.centralMapping.exportMappingData(
      breakdown,
      valueType,
      appliedFilters ?? {},
    );

    this.usageAnalytics.track('Mapping Data Exported', {
      eventTrigger: 'ratecode-export',
    });
  }

  @action
  handleUploadMapping(): void {
    this.args.onClickUploadMapping?.();

    this.usageAnalytics.track('Mapping Upload Started', {
      eventTrigger: 'upload-button-clicked',
      breakdown: this.args.breakdown,
      valueType: this.args.valueType,
    });
  }

  @action onChangePageSize(pageSizeOption: { value: number }): void {
    this.args.onChangePageSize(pageSizeOption.value);
  }

  @action
  onRowCollapseToggle(row: CustomEmberTableRow): void {
    if (!isTableMappingParentRow(row)) {
      return;
    }

    const { id } = row;

    if (id) {
      const isExpanded = !row.isCollapsed;
      const expandedSet = new Set(this.expandedRows);

      if (isExpanded) {
        expandedSet.add(id);
      } else {
        expandedSet.delete(id);
      }

      this.expandedRows = Array.from(expandedSet);
    }
  }

  @action onRowSelect(rows: CustomEmberTableRow | CustomEmberTableRow[]): void {
    const typedRows = (
      Array.isArray(rows) ? rows : [rows]
    ) as TableMappingRow[];

    this.args.setSelectedRowIds(typedRows.map((item) => item['id']));
    this.args.setSelectedMappingEntries(
      this.getMappingEntriesFromSelectedRows(typedRows),
    );
  }

  @action onSelectTab(tabId: string): void {
    if (!isPmsValueType(tabId)) {
      return;
    }

    this.args.setValueType(tabId);

    // Clear the selected rows when the selected tab changes.
    this.clearRowSelections();
  }

  @action openMappingDropdown(): void {
    this.isMappingDropdownOpen = true;
  }

  @action
  setBarBased(
    mappingEntries: CentralMappingMappingEntryExtendedModel[],
    value: BarBased,
  ): void {
    const mappingEntriesWithBarBased = mappingEntries.filter(
      // @ts-expect-error: Incorrect type
      (mappingEntry) => mappingEntry && mappingEntryHasBarBased(mappingEntry),
    );

    const hasChanges = mappingEntriesWithBarBased.some((mappingEntry) => {
      return mappingEntry.userBarBased !== value;
    });

    mappingEntriesWithBarBased.forEach((mappingEntry) => {
      if (!mappingEntry) {
        return;
      }

      // If user deselets and then re-selects the auto-mapped value, it shouldn't count as a change.
      // We prevent userBarBased being set and hasDirtyAttributes becoming true.
      if (
        value === mappingEntry.autoBarBased &&
        isEmpty(mappingEntry.userBarBased)
      ) {
        mappingEntry.userBarBased = null;
      } else {
        mappingEntry.userBarBased = value;
      }
    });

    if (hasChanges) {
      this.showToastSelectionsChanged(mappingEntries);
      this.showToastRowsHidden(mappingEntries);
    }
  }

  @action
  setCategory(
    mappingEntries: CentralMappingMappingEntryExtendedModel[],
    category: CentralMappingCategory | null,
  ): void {
    const hasChanges = mappingEntries.some((mappingEntry) => {
      return mappingEntry.userCategory !== category;
    });

    mappingEntries.forEach((mappingEntry) => {
      mappingEntry.userCategory = category;
    });

    if (hasChanges) {
      this.showToastSelectionsChanged(mappingEntries);
      this.showToastRowsHidden(mappingEntries);
    }
  }

  @action
  setCommissionable(
    mappingEntries: CentralMappingMappingEntryExtendedModel[],
    value: Commissionable,
  ): void {
    const mappingEntriesWithCommissionable = mappingEntries.filter(
      (mappingEntry) =>
        mappingEntry && mappingEntryHasCommissionable(mappingEntry),
    );

    const hasChanges = mappingEntriesWithCommissionable.some((mappingEntry) => {
      return mappingEntry.userCommissionable !== value;
    });

    mappingEntriesWithCommissionable.forEach((mappingEntry) => {
      if (!mappingEntry) {
        return;
      }

      // If user deselects and then re-selects the auto-mapped value, it shouldn't count as a change.
      // We prevent userCommissionable being set and hasDirtyAttributes becoming true.
      if (
        value === mappingEntry.autoCommissionable &&
        isEmpty(mappingEntry.userCommissionable)
      ) {
        mappingEntry.userCommissionable = null;
      } else {
        mappingEntry.userCommissionable = value;
      }
    });

    if (hasChanges) {
      this.showToastSelectionsChanged(mappingEntries);
      this.showToastRowsHidden(mappingEntries);
    }
  }

  @action setFilterOption(filter: FilterOption): void {
    this.args.setFilterOption(filter);

    if (filter !== FILTER_OPTIONS.ALL) {
      this.clearRowSelections();
    }

    this.persistSelectedRows();

    if (filter === FILTER_OPTIONS.UNSAVED) {
      this.usageAnalytics.track('Unsaved Changes Viewed', {
        eventTrigger: 'view-unsaved',
      });
    }
  }

  @action setIsMappingDropdownOpen(isOpen: boolean): void {
    this.isMappingDropdownOpen = isOpen;
  }

  @action setMarketMappingFilters(
    marketMappingFilters: MarketMappingFilter[],
  ): void {
    this.args.setMarketMappingFilters(marketMappingFilters);

    this.persistSelectedRows();
  }

  @action setSearchInputValue(event: Event): void {
    this.searchInputValue = (event.target as HTMLInputElement)?.value;

    this.persistSelectedRows();
  }

  @action
  showToastRowsHidden(
    mappingEntries: CentralMappingMappingEntryExtendedModel[],
  ): void {
    const didAnyRowBecomeHidden = mappingEntries.some((mappingEntry) => {
      return !this.filteredMapping.includes(mappingEntry);
    });

    // Check if mapping any of the rows removed it from the table rows. If so, show a toast to make the user aware of it.
    if (didAnyRowBecomeHidden) {
      this.persistSelectedRows();

      this.toast.success(
        this.intl.t('settings.central_mapping.mapping.toast.hid_row'),
        {
          showIcon: true,
        },
      );
    }
  }

  @action showToastSelectionsChanged(
    mappingEntries: CentralMappingMappingEntryExtendedModel[],
  ): void {
    const isMultipleRows = mappingEntries.length > 1;

    if (isMultipleRows) {
      this.toast.success(
        this.intl.t(
          'settings.central_mapping.mapping.count_selections_changed',
          {
            count: mappingEntries.length,
          },
        ),
        {
          showIcon: true,
        },
      );
    }
  }

  <template>
    <PrismTabsContainer
      @activeTabId={{@valueType}}
      @onSelectTab={{this.onSelectTab}}
      @visibleTabIds={{this.visibleTabIds}}
      as |TabsContainer|
    >
      <PrismCard @contentLayout="noPadding" class="h-full">
        <:content>
          <div
            class="central-mapping__table-mapping__header flex flex-row items-center justify-between"
          >
            {{#if (gt this.visibleTabIds.length 1)}}
              <TabsContainer.header>
                <:default as |Header|>
                  {{#each this.visibleTabIds as |tabId|}}
                    <Header.tab
                      @id={{tabId}}
                      data-test-id="central-mapping-table-mapping-header-tab"
                    >
                      {{t (this.getTabTranslationKey tabId @breakdown)}}
                    </Header.tab>
                  {{/each}}
                </:default>
              </TabsContainer.header>
            {{else}}
              <div class="text-400--bold px-400 py-300">{{t
                  (this.getTabTranslationKey
                    (get this.visibleTabIds 0) @breakdown
                  )
                }}</div>
            {{/if}}

            <div class="flex flex-row items-center gap-200 px-400 py-300">
              {{#unless @isMappingReadOnly}}
                <SettingsCentralMappingDropdownsDropdownFilter
                  @breakdown={{@breakdown}}
                  @filterOption={{@filterOption}}
                  @mapping={{@mapping}}
                  @setFilterOption={{this.setFilterOption}}
                />
              {{/unless}}

              <PrismInput
                @iconAfter={{this.searchIcon}}
                @name="search"
                @onInput={{this.onSearchInputTask.perform}}
                @placeholder={{t
                  "settings.central_mapping.search_on_page.placeholder"
                }}
                @removePadding={{true}}
                @type="text"
                @value={{this.searchInputValue}}
              />

              {{#unless @isLoadingMarketFilters}}
                {{! @glint-expect-error: Incorrect type }}
                <SettingsCentralMappingDropdownsDropdownMappingFilter
                  @appliedFilters={{@appliedFilters}}
                  @applyFilters={{this.applyMappingFilters}}
                  @brandOptions={{@brandOptions}}
                  @chainOptions={{@chainOptions}}
                  @hierarchyCategories={{@hierarchyCategories}}
                  @isOpen={{this.isMappingDropdownOpen}}
                  @onToggleDropdown={{this.setIsMappingDropdownOpen}}
                  @propertyOptions={{@propertyOptions}}
                  @showCommissionableFilter={{this.showCommissionableFilter}}
                />
              {{/unless}}

              <div>
                {{#if @isStaffUser}}
                  <div class={{styles.split-button}}>
                    <PrismButton
                      @buttonType="secondary"
                      @iconBefore="export"
                      @isDisabled={{this.isExportDisabled}}
                      @label={{t "general.export"}}
                      @onClick={{this.handleMappingExport}}
                      data-test-id="central-mapping-export-button"
                    />
                    <PrismComboSelectInput
                      @disabled={{this.isExportDisabled}}
                      @horizontalPosition="right"
                      @onChange={{this.handleUploadMapping}}
                      @options={{this.uploadDropdownOptions}}
                      @unsafe_triggerComponent={{component
                        GeneralDropdownsTriggersTriggerPowerSelectChevron
                        isDisabled=this.isExportDisabled
                      }}
                      data-test-id="central-mapping-upload-dropdown"
                    />
                  </div>
                {{else}}
                  <PrismButton
                    @buttonType="secondary"
                    @iconBefore="export"
                    @isDisabled={{this.isExportDisabled}}
                    @label={{t "general.export"}}
                    @onClick={{this.handleMappingExport}}
                    data-test-id="central-mapping-export-button"
                  />
                {{/if}}

                {{#if this.showExportTooltip}}
                  <PrismTooltip
                    @side="top"
                    @text={{this.exportButtonTooltipText}}
                  />
                {{/if}}
              </div>
            </div>
          </div>

          <div class="central-mapping__table-mapping">
            <PrismTable
              @checkboxSelectionMode="multiple"
              @columns={{this.columns}}
              @disableCheckboxSelection={{this.isMappingReadOnly}}
              @fillMode="last-column"
              @isLoading={{@isLoading}}
              @onRowCollapseToggle={{this.onRowCollapseToggle}}
              @onRowSelect={{this.onRowSelect}}
              @oneCellBulkEditRow={{false}}
              @removeTableBorder={{true}}
              @renderAll={{false}}
              @rowSelection={{this.selectedRows}}
              @rows={{this.rows}}
              class="central-mapping__table-mapping__table"
              data-test-id="table-central-mapping"
            >
              <:bulkEditRow as |bulkEditRow|>
                <MarketSettingsCentralMappingTablesTableMappingBulkEditRow
                  @barBased={{hash
                    setBarBased=(fn this.setBarBased @selectedMappingEntries)
                  }}
                  @breakdown={{@breakdown}}
                  @bulkEditRow={{bulkEditRow}}
                  @commissionable={{hash
                    setCommissionable=(fn
                      this.setCommissionable @selectedMappingEntries
                    )
                  }}
                  @mapping={{hash
                    hierarchyCategories=@hierarchyCategories
                    setCategory=(fn this.setCategory @selectedMappingEntries)
                  }}
                  @selectedMappingEntries={{@selectedMappingEntries}}
                  @valueType={{@valueType}}
                />
              </:bulkEditRow>
              <:emptyState>
                {{#if this.showFilterNudgeEmptyState}}
                  <div
                    class="central-mapping__table-mapping__empty-state flex h-full items-center justify-center"
                  >
                    <PrismMessage
                      @description={{t
                        "settings.central_mapping.mapping.rate_code_empty_state.description"
                      }}
                      @illustration="100x100_binoculars"
                      @title={{t
                        "settings.central_mapping.mapping.rate_code_empty_state.title"
                      }}
                    >
                      <:secondaryAction>
                        <PrismButton
                          @buttonType="secondary"
                          @iconBefore="plus"
                          @label={{t
                            "settings.central_mapping.mapping.rate_code_empty_state.action"
                          }}
                          @onClick={{this.openMappingDropdown}}
                        />
                      </:secondaryAction>
                    </PrismMessage>
                  </div>
                {{else}}
                  <div class="flex h-full items-center justify-center">
                    <PrismMessage
                      @description=""
                      @illustration="100x100_search"
                      @title={{t
                        "settings.central_mapping.mapping.nothing_matches_filters"
                      }}
                    />
                  </div>
                {{/if}}
              </:emptyState>
            </PrismTable>
          </div>
        </:content>

        <:footer>
          {{#if this.showPaginationText}}
            <GeneralTablesPaginationFooter
              @currentPage={{@currentPage}}
              @lastPage={{@lastPage}}
              @onChangePageSize={{this.onChangePageSize}}
              @onSetPage={{@onSetPage}}
              @pageSizeDropdown={{GeneralDropdownsDropdownPageSize}}
              @pageSizeValue={{@curentPageSizeValueOption}}
              @showingText={{this.showingText}}
              @testIdPrefix="central-mapping-table-mapping"
            />
          {{/if}}
        </:footer>
      </PrismCard>
    </PrismTabsContainer>
  </template>
}
